import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  ContainerVideo,
  VideoContainer,
  VideoDegrade,
  VideoTouch,
  VideoContainerPadding,
  Container,
} from './styled';

import OverlayInfos from './OverlayInfos';

import VideoRecommendations from './Recommendation';
import VideoHeader from './Header';
import { ReadPreviewsShow, ReadShowDetails } from '../../services/catalog';
import { exposeStrapiError } from '../../utils';
import { parseVideo, parseVideoShow } from '../../utils/parser';
import { Load } from '../../ui/styled';
import { CoreContext } from '../../context/CoreContext';
import { AddHistory } from '../../services/account';

import useSocket from '../../hooks/useSocket';

import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useNavigate } from '@reach/router';
import useRemoteControl from '../../hooks/useRemoteControl';

// Minimal VideoItem shape mirrored from CoreContext — the context's interface is
// not exported, so we declare a structurally-compatible local type for the state
// setters (id required, extra fields open via index signature).
interface VideoItem {
  id: number | string;
  [key: string]: unknown;
}

// `relateds` is rendered by VideoRecommendations which requires a `cover` —
// parseVideo always seeds it, so the related list carries that field too.
interface RelatedVideoItem extends VideoItem {
  cover: { uri: string };
}

// Back/exit keycodes across the TV platforms we target (Tizen/webOS/browser).
// The cross-origin Bunny iframe owns play/pause/seek once focused (RESEARCH
// Pitfall 1), so the parent handler is scoped to app-level Back/exit only.
const BACK_EXIT_KEYCODES = [10009, 461, 27, 8, 10182, 413];

// Enter/OK/Play keycodes — the one parent-owned affordance before the iframe
// grabs focus: dismiss the pre-play overlay so the autoplaying embed is full
// screen. (D-pad scrub/progress is deferred — Phase 59 device-verify.)
const ENTER_PLAY_KEYCODES = [13, 415, 10252];

// Bunny embed gets exactly the capabilities the player needs — no camera /
// microphone / geolocation (threat T-58-03; asserted in the allow-scope test).
const IFRAME_ALLOW = 'autoplay; fullscreen; encrypted-media; picture-in-picture';

export default withFocusable({
  trackChildren: true,
  forgetLastFocusedChild: false,
})(function VideoPlayer({
  setFocus,
  ...props
}: Record<string, unknown> & { setFocus: (key: string) => void }) {
  const navigate = useNavigate(props);

  const [playing, setPlaying] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  const {
    currentVideo,
    authentication,
    setAuthentication,
    currentProfile,
    setCurrentVideo,
    isOnline,
  } = useContext(CoreContext);

  const { emitPlaying, emitStoping } = useSocket();

  const [loading, setLoading] = useState(false);

  // Dismiss the pre-play overlay so the autoplaying Bunny embed goes full screen.
  // The cross-origin iframe owns true play/pause once focused (RESEARCH Pitfall 1);
  // this is the parent's one pre-focus play affordance, shared by Enter and the
  // overlay's play button.
  const startPlayback = () => {
    if (iframeUrl) {
      setPlaying(true);
    }
  };

  // App-level remote handler. The cross-origin iframe captures play/pause/seek
  // once it has focus; the only keys the parent reliably owns are Back/exit
  // (leave the player) and Enter/OK (dismiss the pre-play overlay).
  const handleRemote = (code: number) => {
    if (BACK_EXIT_KEYCODES.includes(code)) {
      navigate('/catalog');
    } else if (ENTER_PLAY_KEYCODES.includes(code)) {
      startPlayback();
    }
  };

  useRemoteControl(handleRemote);

  const [relateds, setRelateds] = useState<RelatedVideoItem[]>([]);
  const [details, setDetails] = useState<Record<string, unknown>>({});

  const [nextStream, setNextStream] = useState<VideoItem | null>(null);
  const [prevStream, setPrevStream] = useState<VideoItem | null>(null);

  const [videoId, setVideoId] = useState<string | number | null>(null);

  // F1 (3402958071) + F3 (3402975286): the unmount cleanup effect runs with empty
  // deps, so binding videoId/emitStoping directly froze the mount-time values
  // (videoId null, emitStoping closing over a not-yet-connected socket). Mirror the
  // latest values into refs the once-bound cleanup reads — so "emit stop on unmount"
  // fires with the REAL videoId through the connected-socket emitter.
  const videoIdRef = useRef(videoId);
  videoIdRef.current = videoId;
  const emitStopingRef = useRef(emitStoping);
  emitStopingRef.current = emitStoping;

  // loadVideo only runs from the effect after `currentVideo?.id` is truthy, so
  // currentVideo and its id are present here.
  const loadVideo = async (current: { id: string | number; [key: string]: unknown }) => {
    setLoading(true);

    const nvideoObj: VideoItem = { ...current, id: current.id };
    const currentId = current.id;
    setVideoId(currentId);

    // The service signatures require non-undefined token / profileId. At runtime
    // the effect only triggers loadVideo when authentication/profile exist, but
    // strict mode can't see that — fall back to empty so the type narrows.
    const jwt = authentication?.jwt ?? '';
    const profileId = currentProfile?.id ?? '';
    // v5: findOne/filters route by documentId — numeric id 404s (main hotfix,
    // same fix as web 3af6d19f). Fall back to the numeric id for v4 payloads.
    const showRef = (current.documentId as string | undefined) ?? currentId;
    const result = await ReadShowDetails(showRef, jwt);
    const prevShow = await ReadPreviewsShow(showRef, jwt);
    await AddHistory(profileId, { data: { stream_show: showRef } }, jwt);

    if (!exposeStrapiError(result) && result?.iframe_url) {
      // Online streaming via the Bunny iframe player. The backend returns a
      // complete, ready-to-load URL (token + expires + autoplay) — load it
      // verbatim; the thin client owns no Bunny URL logic or secrets. The iframe
      // mounts immediately (autoplay=true); the pre-play overlay (TV navigation
      // chrome) stays mounted over it until the user dismisses it with a key.
      setFocus('play-button');
      setIframeUrl(result.iframe_url);
      setCurrentVideo(null);
    } else if (result?.limit) {
      setCurrentVideo(nvideoObj);
      navigate('/limit');
    } else {
      setAuthentication(null);
      setCurrentVideo(null);
      navigate('/');
    }

    if (prevShow?.data?.[0]?.id) {
      setPrevStream(prevShow?.data?.[0]);
    }

    if (result?.next_stream?.id) {
      setNextStream(result?.next_stream);
    }

    if (result?.related_shows?.length) {
      setRelateds(result.related_shows?.map(parseVideo));
    }
    if (result?.id) {
      setDetails(parseVideoShow(result));
    }
    setLoading(false);
  };

  const nextShow = () => {
    setCurrentVideo(nextStream);
  };

  const prevShow = () => {
    setCurrentVideo(prevStream);
  };

  // loadVideo captures fresh context each render; running it only on
  // currentVideo/isOnline transitions avoids a fetch loop.
  useEffect(() => {
    if (currentVideo?.id && isOnline) {
      loadVideo(currentVideo as { id: string | number; [key: string]: unknown });
    }
  }, [currentVideo, isOnline]);

  // Emit play on the play transition only.
  useEffect(() => {
    if (playing) {
      emitPlaying(videoId);
    }
  }, [playing]);

  // Emit stop once on unmount — read the latest videoId + emitStoping from refs so
  // the empty-deps cleanup never closes over the stale mount-time values (F1/F3).
  useEffect(() => {
    return () => {
      emitStopingRef.current(videoIdRef.current);
    };
  }, []);

  return (
    <Container>
      {loading ? (
        <ContainerVideo purple>
          <Load margin={'auto'} />
        </ContainerVideo>
      ) : (
        <>
          <VideoTouch focusKey={'pause-button'}>
            {iframeUrl ? (
              <iframe
                title="player"
                src={iframeUrl}
                width="100%"
                height="100%"
                allow={IFRAME_ALLOW}
                allowFullScreen
                style={{ border: 0, objectFit: 'contain' }}
              />
            ) : null}
          </VideoTouch>
          {playing ? null : (
            <ContainerVideo>
              <VideoDegrade>
                <VideoContainer>
                  <VideoContainerPadding>
                    <VideoHeader details={details} />
                    <OverlayInfos
                      details={details}
                      playPause={startPlayback}
                      nextShow={nextStream ? nextShow : null}
                      prevShow={prevStream ? prevShow : null}
                    />
                  </VideoContainerPadding>
                  <VideoRecommendations relateds={relateds} />
                </VideoContainer>
              </VideoDegrade>
            </ContainerVideo>
          )}
        </>
      )}
    </Container>
  );
});
